<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Danh sách phiếu đề nghị mua hàng'">
        <template v-slot:toolbar>
          <b-button
            v-if="checkPermission('PR_INSERT')"
            variant="primary"
            size="sm"
            @click="addNew"
          >
            <i class="fas fa-plus"></i> Tạo phiếu
          </b-button>
          <b-button
            v-if="checkPermission('PR_INSERT')"
            variant="success"
            size="sm"
            class="ml-2"
            @click="goToImport"
          >
            <i class="fas fa-file-excel"></i> Import excel
          </b-button>
        </template>

        <template v-slot:preview>
          <!-- Filters -->
          <b-row class="mb-3">
            <b-col cols="2">
              <b-form-select
                v-model="filterStatus"
                :options="statusOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="2">
              <BDropdownCustom
                v-model="filterDepartmentId"
                :options="departmentDropdownOptions"
                :placeholder="'phòng ban'"
                :matchKey="['name']"
                :searchable="true"
                :limit="5"
                :showResizeHorizontal="true"
                :compareById="true"
              />
            </b-col>
            <b-col cols="2">
              <BDropdownCustom
                v-model="filterCostItemId"
                :options="costItemDropdownOptions"
                :placeholder="'khoản mục'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="2">
              <BDropdownCustom
                v-model="filterStoreId"
                :options="storeDropdownOptions"
                :placeholder="'cửa hàng'"
                :matchKey="['name']"
                :searchable="true"
                :compareById="true"
                :limit="5"
              />
            </b-col>
            <b-col cols="2">
              <date-picker
                v-model="filterPeriod"
                :config="dpConfigs.month"
                class="form-control-sm"
                placeholder="Chọn tháng"
              ></date-picker>
            </b-col>
            <b-col cols="2">
              <b-form-select
                v-model="filterCompanyId"
                :options="companyOptions"
                size="sm"
              />
            </b-col>
          </b-row>
          <b-row class="mb-5">
            <b-col cols="2">
              <b-form-input
                v-model="filterCode"
                size="sm"
                placeholder="Tìm theo mã phiếu"
                @keyup.enter="onFilter"
              />
            </b-col>
            <b-col cols="4">
              <b-form-input
                v-model="filterDescription"
                size="sm"
                placeholder="Từ khóa diễn giải..."
                @keyup.enter="onFilter"
              />
            </b-col>
            <b-col cols="4">
              <Autosuggest
                :model.sync="employeeModel"
                :suggestions="employeeSuggestions"
                :limit="suggestLimit"
                suggestionName="suggestionName"
                placeholder="người tạo"
                @select="onSelectedEmployee"
                @change="onSearchEmployeeChange"
              />
            </b-col>
            <b-col cols="2" class="d-flex align-items-center">
              <b-form-checkbox
                v-model="filterMyApproval"
                class="mr-3"
                @change="onFilter"
              >
                Cần tôi duyệt
              </b-form-checkbox>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col cols="2">
              <b-form-select
                v-model="filterSortBy"
                :options="sortByOptions"
                size="sm"
              />
            </b-col>
            <b-col cols="10">
              <b-button variant="primary" size="sm" @click="onFilter" class="mr-2">
                <i class="fas fa-search"></i> Lọc
              </b-button>
            </b-col>
          </b-row>

          <!-- Table -->
          <b-table
            :items="items"
            :fields="fields"
            bordered
            hover
            :busy="onLoading"
          >
            <template #cell(index)="data">
              {{ (page - 1) * 20 + data.index + 1 }}
            </template>
            <template #cell(code)="data">
                <a href="#" @click.prevent="viewDetail(data.item)">
                  <strong>{{ data.value }}</strong>
                </a>
                <span v-if='data.item.hrmFinanceExternalCode'>
                  <br/>
                  <i class="fas fa-link"></i>
                  <br/>
                  <span
                    class="text-success font-weight-bold"
                    v-b-tooltip.hover="hrmFinanceExternalStatusTooltip(data.item)"
                    style="cursor: help"
                  >
                    <strong>{{ data.item.hrmFinanceExternalCode }}</strong>
                  </span>
                </span>
            </template>
            <template #cell(info)="row">
              <div>
                <div>
                  <span class="font-weight-bold">Doanh nghiệp:</span>
                  <span class="ml-1">{{ row.item.companyName || row.item.companyId }}</span>
                </div>
                <div>
                  <span class="font-weight-bold">Phòng ban:</span>
                  <span class="ml-1">{{ getDepartmentName(row.item) }}</span>
                </div>
                <div v-if="row.item.lineCount > 0">
                  <span class="font-weight-bold">Khoản mục:</span>
                  <span class="ml-1">
                    <template v-if="row.item.costItemNames && row.item.costItemNames.length">
                      {{ row.item.costItemNames[0] }}
                      <b-badge v-if="row.item.costItemNames.length > 1" variant="info" class="ml-1">
                        +{{ row.item.costItemNames.length - 1 }}
                      </b-badge>
                    </template>
                    <template v-else>{{ getCostItemName(row.item) }}</template>
                  </span>
                </div>
                <div v-if="row.item.lineCount > 0">
                  <span class="font-weight-bold">Cửa hàng:</span>
                  <span class="ml-1">
                    <template v-if="row.item.storeNames && row.item.storeNames.length">
                      {{ row.item.storeNames[0] }}
                      <b-badge v-if="row.item.storeNames.length > 1" variant="secondary" class="ml-1">
                        +{{ row.item.storeNames.length - 1 }}
                      </b-badge>
                    </template>
                    <template v-else>{{ getStoreName(row.item) }}</template>
                  </span>
                </div>
                <div v-if="row.item.lineCount > 0" class="mt-1">
                  <b-badge variant="light" class="border">{{ row.item.lineCount }} dòng</b-badge>
                  <b-badge v-if="row.item.storeCount > 0" variant="light" class="border ml-1">{{ row.item.storeCount }} cửa hàng</b-badge>
                </div>
              </div>
            </template>
            <template #cell(requestedAmount)="data">
              {{ convertPrice(data.item.totalAmount || data.value) }}
            </template>
            <template #cell(status)="data">
              <b-badge
                :variant="prStatusVariant(data.value)"
                style="cursor: pointer"
                @click="openApprovalWorkflow(data.item)"
              >
                {{ prStatusName(data.value) }}
              </b-badge>
            </template>
            <template #cell(nextApprovalStep)="data">
              <span style="cursor: pointer" @click="openApprovalWorkflow(data.item)">
                <ApprovalStepBadge :step="data.value" />
                <small class="text-muted" v-if="data.item.nextApprovalStep?.updatedAt" v-b-tooltip.hover title="Ngày cập nhật trạng thái"> {{ formatDate(data.item.nextApprovalStep?.updatedAt, CLIENT_DATE_TIME_NO_SECONDS) }} </small>
              </span>
            </template>
            <template #cell(createdBy)="row">
              {{ getEmployeeName(row.item) }}
            </template>
            <template #cell(createdAt)="data">
              {{ formatDateTime(data.value) }}
            </template>
            <template v-slot:cell(actions)="row">
              <div class="d-flex justify-content-center">
                <b-dropdown size="sm" id="dropdown-left" no-caret right>
                  <template #button-content>
                    <i class="flaticon2-settings"></i>
                  </template>
                  <b-dropdown-item @click="viewDetail(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-sheet icon-size-sm"></i>
                      &nbsp; Xem chi tiết
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="showHistory(row.item)">
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-time icon-size-sm"></i>
                      &nbsp; Lịch sử
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canApprove"
                    @click="approveItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-checkmark icon-size-sm"></i>
                      &nbsp; Duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canReject"
                    @click="rejectItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="flaticon2-cross icon-size-sm"></i>
                      &nbsp; Từ chối
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canUndo"
                    @click="undoItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="fas fa-undo icon-size-sm"></i>
                      &nbsp; Hoàn duyệt
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="row.item.canEdit && checkPermission('PR_UPDATE')"
                    @click="editItem(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="fas fa-edit icon-size-sm"></i>
                      &nbsp; Chỉnh sửa
                    </span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="
                      canDeleteItem(row.item) && checkPermission('PR_UPDATE')
                    "
                    @click="openDeleteModal(row.item)"
                  >
                    <span style="color: #3f4254; font-size: 10px">
                      <i class="fas fa-trash-alt icon-size-sm"></i>
                      &nbsp; Xoá phiếu
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>
          </b-table>

          <b-row class="mt-3 align-items-center">
            <b-col cols="12" md="4">
              Tổng số: <strong>{{ totalRow }}</strong>
            </b-col>
            <b-col cols="12" md="8" class="d-flex justify-content-end">
              <BPaginationNavCustom
                :currentPage="page"
                :numberOfPages="numberOfPage"
                :totalPage="numberOfPage"
                :linkParams="paginationLinkParams"
                @page-change="fetchData"
              />
            </b-col>
          </b-row>

          <!-- Modal xóa phiếu (chờ duyệt) -->
          <b-modal
            ref="deleteModal"
            title="Xoá phiếu đề nghị mua hàng"
            @ok="confirmDelete"
            ok-variant="danger"
            ok-title="Xoá"
            cancel-title="Đóng"
          >
            <p class="mb-0">
              Bạn có chắc muốn xoá phiếu <strong>{{ deleteForm.code }}</strong
              >?
            </p>
          </b-modal>

          <!-- Modal duyệt -->
          <b-modal
            ref="approveModal"
            title="Phê duyệt phiếu"
            @ok="confirmApprove"
            ok-variant="success"
            ok-title="Phê duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú">
              <b-form-textarea
                v-model="approveForm.note"
                rows="2"
                placeholder="Nhập ghi chú duyệt"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal từ chối -->
          <b-modal
            ref="rejectModal"
            title="Từ chối phiếu"
            @ok="confirmReject"
            ok-variant="danger"
            ok-title="Từ chối"
            cancel-title="Đóng"
          >
            <b-form-group>
              <template #label>
                Lý do từ chối <span class="text-danger">*</span>
              </template>
              <b-form-textarea
                v-model="rejectForm.rejectionNote"
                rows="3"
                required
              />
            </b-form-group>
          </b-modal>

          <!-- Modal hoàn duyệt -->
          <b-modal
            ref="undoModal"
            title="Hoàn duyệt phiếu"
            @ok="confirmUndo"
            ok-variant="warning"
            ok-title="Hoàn duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú">
              <b-form-textarea
                v-model="undoForm.note"
                rows="2"
                placeholder="Nhập ghi chú hoàn duyệt"
              />
            </b-form-group>
          </b-modal>

          <PurchaseRequestApprovalModal
            ref="approvalModal"
            :requestId="approvalModalRequestId"
            :lines="approvalModalLines"
          />

          <PurchaseRequestHistoryModal
            ref="historyModal"
            :histories="historyItems"
            :fields="historyFields"
          />
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { checkPermission, getData } from '@/utils/saveDataToLocal';
import {
  makeToastSuccess,
  makeToastFaile,
  convertPrice,
  handleError,
  xoa_dau,
} from '@/utils/common';
import moment from 'moment';
import timeUtils from '@/utils/date';
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue';
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue';
import Autosuggest from '@/view/base/auto-sugguest/AutoSuggest.vue';
import ApprovalStepBadge from '@/view/base/bootstrap/ApprovalStepBadge.vue';
import PurchaseRequestApprovalModal from './components/PurchaseRequestApprovalModal.vue';
import PurchaseRequestHistoryModal from './components/PurchaseRequestHistoryModal.vue';
import {
  PR_STATUS,
  PR_STATUS_BADGE_VARIANT,
  WORKFLOW_APPROVAL_STEP_STATUS,
} from '@/utils/enum';
import { PR_STATUS_NAME, HISTORY_ACTION_TYPE_CODE } from '@/utils/enum-name';
import { formatSpecificDate, YEAR_MONTH_DAY_FORMAT, formatDate, CLIENT_DATE_TIME_NO_SECONDS } from '@/utils/date';

export default {
  components: {
    KTCodePreview,
    BDropdownCustom,
    BPaginationNavCustom,
    Autosuggest,
    ApprovalStepBadge,
    PurchaseRequestApprovalModal,
    PurchaseRequestHistoryModal,
  },
  data() {
    return {
      PR_STATUS,
      CLIENT_DATE_TIME_NO_SECONDS,
      items: [],
      onLoading: false,
      page: 1,
      numberOfPage: 1,
      totalRow: 0,
      filterStatus: null,
      filterDepartmentId: [],
      filterCostItemId: [],
      filterStoreId: [],
      filterCompanyId: null,
      filterPeriod: '',
      filterCode: '',
      filterDescription: '',
      filterCreatedBy: null,
      filterMyApproval: false,
      filterSortBy: 'status',
      employeeModel: '',
      suggestLimit: 50,
      employeeSource: [],
      employeeSuggestions: [],
      departmentDropdownOptions: [],
      costItemDropdownOptions: [],
      storeDropdownOptions: [],
      companyOptions: [{ value: null, text: '-- Doanh nghiệp --' }],
      approvalModalRequestId: null,
      approvalModalLines: [],
      sortByOptions: [
        { value: 'status', text: 'Sắp xếp theo trạng thái' },
        { value: 'createdAt', text: 'Sắp xếp theo ngày tạo' },
      ],
      statusOptions: [
        { value: null, text: '-- Trạng thái --' },
        { value: PR_STATUS.PENDING, text: PR_STATUS_NAME[PR_STATUS.PENDING] },
        { value: PR_STATUS.BROWSING, text: PR_STATUS_NAME[PR_STATUS.BROWSING] },
        { value: PR_STATUS.APPROVED, text: PR_STATUS_NAME[PR_STATUS.APPROVED] },
        { value: PR_STATUS.REJECTED, text: PR_STATUS_NAME[PR_STATUS.REJECTED] },
        { value: PR_STATUS.CANCELED, text: PR_STATUS_NAME[PR_STATUS.CANCELED] },
      ],
      currentEmployeeId: null,
      deleteForm: { id: null, code: '' },
      approveForm: { id: null, note: '' },
      rejectForm: { id: null, rejectionNote: '' },
      undoForm: { id: null, note: '' },
      historyItems: [],
      historyFields: [
        { key: 'createdAt', label: 'Lúc', thStyle: { width: '15%' } },
        { key: 'actionLabel', label: 'Hành động', thStyle: { width: '15%' } },
        { key: 'description', label: 'Chi tiết' },
        {
          key: 'updatedBy',
          label: 'Người thực hiện',
          thStyle: { width: '20%' },
        },
      ],
      dpConfigs: {
        // Used for month-only filtering on `period` (send `${filterPeriod}-01` to BE)
        month: {
          ...timeUtils.DP_CONFIG.date,
          format: 'YYYY-MM',
          viewMode: 'months',
        },
      },
      fields: [
        {
          key: 'index',
          label: 'STT',
          thStyle: { textAlign: 'center', width: '3%' },
          class: 'text-center',
        },
        {
          key: 'code',
          label: 'Mã phiếu',
          thStyle: { textAlign: 'center', width: '6%' },
        },
        {
          key: 'period',
          label: 'Kỳ ngân sách',
          thStyle: { textAlign: 'center', width: '6%' },
          class: 'text-center',
          formatter: (value) => formatSpecificDate(value, YEAR_MONTH_DAY_FORMAT, 'MM/YYYY'),
        },
        {
          key: 'info',
          label: 'Thông tin',
          thStyle: { textAlign: 'left', width: '22%' },
        },
        {
          key: 'requestedAmount',
          label: 'Số tiền',
          thStyle: { textAlign: 'center', width: '10%' },
          tdClass: 'text-right',
        },
        {
          key: 'nextApprovalStep',
          label: 'Bước duyệt',
          thStyle: { textAlign: 'center', width: '8%' },
          class: 'text-center',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          thStyle: { textAlign: 'center', width: '8%' },
          class: 'text-center',
        },
        {
          key: 'createdBy',
          label: 'Người tạo',
          thStyle: { textAlign: 'center', width: '9%' },
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          thStyle: { textAlign: 'center', width: '9%' },
          class: 'text-center',
        },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '4%' },
          class: 'text-center',
        },
      ],
      linkGen: (pageNum) => (pageNum === 1 ? '?' : `?page=${pageNum}`),
    };
  },
  computed: {
    paginationLinkParams() {
      return {
        status: this.filterStatus || '',
        departmentId: this.filterDepartmentId.map((d) => d.id),
        costItemId: this.filterCostItemId.map((c) => c.id),
        storeId: this.filterStoreId.map((s) => s.id),
        companyId: this.filterCompanyId || '',
        period: this.filterPeriod || '',
        codeSearch: this.filterCode || '',
        descriptionSearch: this.filterDescription || '',
        createdBy: this.filterCreatedBy || '',
        sortBy: this.filterSortBy || 'status',
      };
    },
  },
  created() {
    const empInfoRaw = getData('employee_info');
    if (empInfoRaw) {
      try {
        const parsed = JSON.parse(empInfoRaw);
        this.currentEmployeeId = parsed?.id || parsed?._id || null;
      } catch (e) {
        this.currentEmployeeId = null;
      }
    }
    this.applyQueryFilters();
    this.fetchFilterOptions();
    this.fetchEmployees();
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Phiếu đề nghị mua hàng' },
    ]);
  },
  methods: {
    checkPermission,
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    formatDate,
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    prStatusVariant(val) {
      return PR_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    prStatusName(val) {
      return PR_STATUS_NAME[val] || val;
    },
    hrmFinanceExternalStatusTooltip(item) {
      const status = Number(item?.hrmFinanceExternalStatus);
      // HRM FinanceExternalStatusEnum: 1 PENDING, 2 APPROVED, 3 REJECTED, 4 APPROVING, 5 DRAFT
      const statusName = {
        1: 'Chờ duyệt',
        2: 'Đã duyệt',
        3: 'Không duyệt',
        4: 'Đang duyệt',
        5: 'Nháp',
      }[status];

      return statusName ? `Trạng thái phiếu chi: ${statusName}` : 'Chưa có trạng thái phiếu chi';
    },
    getDepartmentName(item) {
      return item?.departmentName || item?.departmentId || '';
    },
    getCostItemName(item) {
      return item?.costItemName || item?.costItemId || '';
    },
    getStoreName(item) {
      if (!item?.storeId) return '—';
      return item?.storeName || item?.storeId || '';
    },
    getEmployeeName(item) {
      if (!item?.createdBy) return '';
      return item?.employeeName || item?.createdBy || '';
    },
    applyQueryFilters() {
      const q = this.$route?.query || {};
      this.page = q.page || 1;

      this.filterStatus = q.status !== undefined && q.status !== '' ? q.status : null;
      this.filterSortBy = q.sortBy || 'status';
      this.filterCompanyId = q.companyId ? Number(q.companyId) : null;
      this.filterCode = q.codeSearch ? String(q.codeSearch) : '';
      this.filterDescription = q.descriptionSearch ? String(q.descriptionSearch) : '';

      this.filterCreatedBy = q.createdBy ? Number(q.createdBy) : null;
      if (!this.filterCreatedBy) {
        this.employeeModel = this.employeeModel && this.employeeModel.trim() ? this.employeeModel : '';
      }

      // Period query is sent to BE as YYYY-MM-DD, but UI picker uses YYYY-MM.
      if (q.period) {
        const raw = String(q.period);
        const m = moment(raw, ['YYYY-MM', 'YYYY-MM-DD'], true);
        this.filterPeriod = m.isValid() ? m.format('YYYY-MM') : raw.slice(0, 7);
      } else {
        this.filterPeriod = '';
      }

      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : [])
          .map(Number)
          .filter((n) => n > 0);
      if (this.departmentDropdownOptions.length) {
        const ids = toIds(q.departmentId);
        this.filterDepartmentId = this.departmentDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.costItemDropdownOptions.length) {
        const ids = toIds(q.costItemId);
        this.filterCostItemId = this.costItemDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
      if (this.storeDropdownOptions.length) {
        const ids = toIds(q.storeId);
        this.filterStoreId = this.storeDropdownOptions.filter((o) =>
          ids.includes(Number(o.id)),
        );
      }
    },
    fetchFilterOptions() {
      const q = this.$route?.query || {};
      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : []).map(Number).filter((n) => n > 0);
      // Departments
      ApiService.query('departments/all').then(({ data }) => {
        const rows = data.data || [];
        this.departmentDropdownOptions = rows.map((d) => ({ id: d.id, name: d.name }));
        if (!this.filterDepartmentId.length) {
          const ids = toIds(q.departmentId);
          this.filterDepartmentId = this.departmentDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      });
      // Cost items
      ApiService.get('v2/cost-items/all').then(({ data }) => {
        const rows = data.data || [];
        this.costItemDropdownOptions = rows.map((c) => ({ id: c.id, name: c.name }));
        if (!this.filterCostItemId.length) {
          const ids = toIds(q.costItemId);
          this.filterCostItemId = this.costItemDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      });
      // Stores
      const params = {
        byEmployeeGroup: true,
      };
      ApiService.query('stores/getStores', { params }).then(({ data }) => {
        const rows = data.data || [];
        this.storeDropdownOptions = rows.map((s) => ({
          id: s.id,
          name: s.shortName ? `${s.name} (${s.shortName})` : s.name,
          shortName: s.shortName,
        }));
        if (!this.filterStoreId.length) {
          const ids = toIds(q.storeId);
          this.filterStoreId = this.storeDropdownOptions.filter((o) =>
            ids.includes(Number(o.id)),
          );
        }
      });
      // Companies
      ApiService.get('/company').then(({ data }) => {
        const rows = data.data || [];
        this.companyOptions = [
          { value: null, text: '-- Doanh nghiệp --' },
          ...rows.map((c) => ({ value: c.id, text: c.name })),
        ];
      });
    },
    fetchEmployees() {
      ApiService.get('/employees/get-list-filter').then((response) => {
        const employees = response?.data?.data || [];
        this.employeeSource = employees.map((e) => {
          let suggestionName = e.fullName || e.name || '';
          if (e.code) suggestionName += ` (${e.code})`;
          const rawName = e.fullName || e.name || '';
          return {
            id: e.id,
            name: rawName,
            code: e.code || '',
            suggestionName,
            nameNoneSign: xoa_dau(rawName),
          };
        });
        this.employeeSuggestions = [...this.employeeSource];
        if (this.filterCreatedBy) {
          const found = this.employeeSource.find((e) => Number(e.id) === Number(this.filterCreatedBy));
          if (found) this.employeeModel = found.suggestionName;
        }
      });
    },
    onSelectedEmployee(option) {
      const item = option?.item || null;
      this.filterCreatedBy = item?.id || null;
      this.employeeModel = item ? item.suggestionName : '';
      this.employeeSuggestions = [...this.employeeSource];
    },
    onSearchEmployeeChange(text) {
      const t = text != null ? String(text) : '';
      this.employeeModel = t;
      if (!t.trim()) {
        this.filterCreatedBy = null;
        this.employeeSuggestions = [...this.employeeSource];
        return;
      }
      const tl = t.toLowerCase();
      this.employeeSuggestions = (this.employeeSource || [])
        .filter((item) => {
          return (
            (item.nameNoneSign || '').toLowerCase().indexOf(tl) > -1 ||
            (item.name || '').toLowerCase().indexOf(tl) > -1 ||
            (item.code || '').toLowerCase().indexOf(tl) > -1
          );
        })
        .slice(0, this.suggestLimit);
    },
    onFilter() {
      this.$router
        .push({
          name: 'list-purchase-requests',
          query: { ...this.paginationLinkParams, page: 1 },
        })
        .catch(() => {})
        .finally(() => this.fetchData());
    },
    openApprovalWorkflow(item) {
      if (!item || !item.id) return;
      this.approvalModalRequestId = item.id;
      this.approvalModalLines = item.lines || [];
      this.$nextTick(() => {
        this.$refs.approvalModal?.open();
      });
    },
    historyActionLabel(action) {
      return HISTORY_ACTION_TYPE_CODE[action] || String(action || '');
    },
    showHistory(item) {
      if (!item || !item.id) return;
      ApiService.get(`v2/purchase-requests/${item.id}/history`)
        .then(({ data }) => {
          if (data.status === 200) {
            this.historyItems = (data.data || []).map((h) => ({
              ...h,
              actionLabel: this.historyActionLabel(h.action),
            }));
            this.$nextTick(() => {
              this.$refs.historyModal?.open();
            });
          } else {
            this.makeToastFaile(data.message);
          }
        })
        .catch((err) => handleError(err));
    },
    fetchData() {
      this.onLoading = true;
      this.page = this.$route.query.page || 1;
      const q = this.$route.query || {};
      const toIds = (val) =>
        (Array.isArray(val) ? val : val ? [val] : []).map(Number).filter((n) => n > 0);
      const params = {
        page: this.page,
        limit: 20,
        status: this.filterStatus || '',
        // Khi options chưa load xong (filter rỗng), dùng trực tiếp từ URL như ListOrder.vue
        departmentId: this.filterDepartmentId.length
          ? this.filterDepartmentId.map((d) => d.id)
          : toIds(q.departmentId),
        costItemId: this.filterCostItemId.length
          ? this.filterCostItemId.map((c) => c.id)
          : toIds(q.costItemId),
        storeId: this.filterStoreId.length
          ? this.filterStoreId.map((s) => s.id)
          : toIds(q.storeId),
        companyId: this.filterCompanyId || '',
        codeSearch: this.filterCode || '',
        descriptionSearch: this.filterDescription || '',
        createdBy: this.filterCreatedBy || '',
        myApproval: this.filterMyApproval || '',
        sortBy: this.filterSortBy || 'status',
      };
      if (this.filterPeriod) {
        const raw = String(this.filterPeriod);
        // Expect BE `period` as YYYY-MM-DD (day fixed to 01).
        // date-picker month mode usually returns `YYYY-MM`.
        const m = moment(
          raw,
          ['YYYY-MM', 'YYYY-MM-DD', 'MM/YYYY', 'DD/MM/YYYY'],
          true,
        );
        params.period = m.isValid()
          ? m.format('YYYY-MM-DD')
          : raw.length >= 7
          ? `${raw.slice(0, 7)}-01`
          : `${raw}-01`;
      }

      ApiService.query('v2/purchase-requests', { params }).then(({ data }) => {
        if (data.status === 200) {
          this.items = data.data.dataset;
          this.numberOfPage = data.data.totalPage;
          this.totalRow = data.data.totalRow || 0;
        } else {
          this.makeToastFaile(data.message);
        }
        this.onLoading = false;
      }).catch((err) => handleError(err));
    },
    addNew() {
      this.$router.push({ name: 'add-purchase-request' });
    },
    goToImport() {
      this.$router.push({ name: 'import-purchase-requests' });
    },
    editItem(item) {
      this.$router.push({
        name: 'edit-purchase-request',
        query: { id: item.id },
      });
    },
    viewDetail(item) {
      this.$router.push({
        name: 'detail-purchase-request',
        query: { id: item.id, from: this.$route.fullPath },
      });
    },
    canDeleteItem(item) {
      if (!this.currentEmployeeId || !item?.createdBy) return false;
      if (
        item.status !== PR_STATUS.PENDING &&
        item.status !== PR_STATUS.BROWSING
      ) {
        return false;
      }
      return Number(item.createdBy) === Number(this.currentEmployeeId);
    },
    openDeleteModal(item) {
      this.deleteForm = { id: item.id, code: item.code || '' };
      this.$refs.deleteModal.show();
    },
    confirmDelete() {
      if (!this.deleteForm?.id) return;
      ApiService.post('v2/purchase-requests/soft-delete', {
        id: this.deleteForm.id,
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
    approveItem(item) {
      this.approveForm = { id: item.id, note: '' };
      this.$refs.approveModal.show();
    },
    rejectItem(item) {
      this.rejectForm = { id: item.id, rejectionNote: '' };
      this.$refs.rejectModal.show();
    },
    undoItem(item) {
      this.undoForm = { id: item.id, note: '' };
      if (this.$refs.undoModal) this.$refs.undoModal.show();
    },
    patchPurchaseRequestChangeStatus(id, status, note) {
      return ApiService.patch(`v2/purchase-requests/change-status/${id}`, {
        status,
        note: note != null ? String(note) : '',
      });
    },
    confirmApprove() {
      if (!this.approveForm?.id) return;
      this.patchPurchaseRequestChangeStatus(
        this.approveForm.id,
        WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        this.approveForm.note || '',
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
    confirmReject() {
      if (!this.rejectForm?.id) return;
      if (
        !this.rejectForm.rejectionNote ||
        !this.rejectForm.rejectionNote.trim()
      ) {
        this.makeToastFaile('Vui lòng nhập lý do từ chối!');
        return;
      }
      this.patchPurchaseRequestChangeStatus(
        this.rejectForm.id,
        WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
        this.rejectForm.rejectionNote,
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
    confirmUndo() {
      if (!this.undoForm?.id) return;
      this.patchPurchaseRequestChangeStatus(
        this.undoForm.id,
        WORKFLOW_APPROVAL_STEP_STATUS.REFUND_APPROVED,
        this.undoForm.note || '',
      )
        .then(({ data }) => {
          this.makeToastSuccess(data.message);
          this.fetchData();
        })
        .catch((err) => handleError(err));
    },
  },
};
</script>
